import { useEffect, useMemo, useState } from 'react';

import { Collapse } from '@mui/material';
import round from 'lodash/round';
import { useField } from 'react-final-form';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  DataPopper,
  DataPopperRow,
  Icons,
  Line,
  Select,
  XAxis,
  YAxis,
  LinkBehaviour,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { toBig } from '@cast/utils';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';

import { OptimizationThresholdType } from '../../../../_types';

import { useControlsGroupState } from '@cast/shared-rff-ui';

type DataPoint = {
  resource: number;
  percentage: number;
  threshold: number;
};

const generateThresholdData = (): DataPoint[] => {
  const data = [];
  for (let x = 0; x <= 50; x = toBig(x).add(0.1).toNumber()) {
    const y = toBig(0.5).div(toBig(x).add(1));
    data.push({
      resource: x,
      percentage: y.mul(100).toNumber(),
      threshold: y.mul(x).toNumber(),
    });
  }

  return data;
};

type ResourceOption = {
  value: string;
  label: string;
};

const options: ResourceOption[] = [
  { value: 'CPU', label: 'CPU' },
  { value: 'GiB', label: 'Memory' },
];

const X_TICK_COUNT = 6;

export const DynamicThresholdSimulation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const data = useMemo(() => generateThresholdData(), []);
  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'threshold simulation',
      getDefaultRange: undefined,
    }),
  });

  const { namePrefix } = useControlsGroupState();
  const cpuThresholdType = useField(
    `${namePrefix}.optimizationThreshold.cpu.type`
  ).input.value;
  const memoryThresholdType = useField(
    `${namePrefix}.optimizationThreshold.memory.type`
  ).input.value;

  const isCPUDynamic = cpuThresholdType === OptimizationThresholdType.DYNAMIC;
  const isMemoryDynamic =
    memoryThresholdType === OptimizationThresholdType.DYNAMIC;

  const [simulationResource, setSimulationResource] = useState<ResourceOption>(
    options[isCPUDynamic ? 0 : 1]
  );

  useEffect(() => {
    setSimulationResource(options[isCPUDynamic ? 0 : 1]);
  }, [isCPUDynamic]);

  if (!isCPUDynamic && !isMemoryDynamic) {
    return null;
  }

  return (
    <Stack data-testid="dynamic-threshold-simulation">
      <LinkBehaviour
        variant="P12M"
        display="flex"
        alignItems="center"
        gap={4}
        mb={8}
        onClick={() => setIsExpanded(!isExpanded)}
        testId="dynamic-threshold-simulation-expander"
      >
        {isExpanded ? 'Hide' : 'Show'} simulation
        {isExpanded ? (
          <Icons.CaretUp size={16} />
        ) : (
          <Icons.CaretDown size={16} />
        )}
      </LinkBehaviour>
      <Collapse in={isExpanded}>
        <Stack
          border={tokens.theme.border.structural.neutral}
          bgcolor={tokens.theme.bg.structural.surface}
          borderRadius="4px"
          px={16}
          pt={8}
          pb={12}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            height={28}
          >
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
            >
              Dynamic threshold Simulation
            </Typography>
            {isCPUDynamic && isMemoryDynamic && (
              <Select
                sx={{ width: 87 }}
                value={simulationResource}
                onChange={(selected) => setSimulationResource(selected)}
                options={options}
                testId="resource-select"
                size="sm"
                optionLabel="label"
              />
            )}
          </Stack>
          <Box position="relative" height={160}>
            <ChartContainer
              data={data}
              sx={{ backgroundColor: 'transparent' }}
              analyzeWithOpsPilot={analyzeWithOpsPilot}
            >
              <ChartGrid hideVertical />

              <Line<DataPoint>
                dataKey="percentage"
                stroke={tokens.theme.fg.selection.primary}
                strokeWidth={2}
              />

              <XAxis
                dataKey="resource"
                ticks={[0, 10, 20, 30, 40, 50]}
                tickFormatter={(t, i) => {
                  const isLast = i + 1 === X_TICK_COUNT;
                  return isLast
                    ? `${t} ${simulationResource.value}`
                    : String(t);
                }}
              />

              <YAxis<DataPoint>
                ticks={[0, 20, 40, 60]}
                unitFormatter={(v) => `${v}%`}
                dataKey="percentage"
              />

              <ChartTooltip<DataPoint>
                TooltipComponent={({ payload }) => {
                  const tooltipData = payload?.[0]?.payload;
                  if (!tooltipData) {
                    return null;
                  }
                  const { resource, percentage, threshold } = tooltipData;

                  return (
                    <DataPopper size="small" sx={{ width: 214 }}>
                      <DataPopperRow
                        sx={{ color: tokens.theme.fg.structural.secondary }}
                        left={
                          <Typography variant="P10R">
                            Threshold value:
                          </Typography>
                        }
                        right={
                          <Typography variant="L10B">
                            {round(threshold, 3)} {simulationResource.value}
                          </Typography>
                        }
                        dividerBottom
                      />
                      <DataPopperRow
                        left={
                          <Typography variant="L10R">
                            {round(percentage, 2)}%
                          </Typography>
                        }
                        right={
                          <Stack direction="row" gap={4}>
                            <Typography variant="L10R">
                              {round(resource, 2)}
                            </Typography>
                            <Typography
                              variant="L10R"
                              color={tokens.theme.fg.structural.secondary}
                            >
                              {simulationResource.value}
                            </Typography>
                          </Stack>
                        }
                      />
                    </DataPopper>
                  );
                }}
              />
            </ChartContainer>
          </Box>
        </Stack>
      </Collapse>
    </Stack>
  );
};
